import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

/**
 * DailyBonusCard — floating claim card for the daily login streak bonus.
 * Shows once per day on the feed when the bonus is claimable; dismissal
 * is remembered for the day via localStorage.
 */
const DISMISS_KEY = "plyx_daily_dismissed";
const todayStr = () => new Date().toISOString().slice(0, 10);

const DailyBonusCard = () => {
  const { user, refresh } = usePlyxAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    if (localStorage.getItem(DISMISS_KEY) === todayStr()) return;
    api.get("/plyx/daily/status")
      .then((r) => {
        if (r.data?.claimable) { setStatus(r.data); setOpen(true); }
      })
      .catch(() => {});
  }, [user]);

  if (!open || !status) return null;

  const table = status.reward_table || [];
  const day = Math.min(status.eligible_streak_day, table.length);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, todayStr()); setOpen(false); };

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await api.post("/plyx/daily/claim");
      toast.success(`🎁 +${r.data.awarded_coins} coins claimed — day ${r.data.streak} streak! 🔥`, { duration: 5000 });
      localStorage.setItem(DISMISS_KEY, todayStr());
      setOpen(false);
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim — try again");
      setOpen(false);
    } finally { setClaiming(false); }
  };

  return (
    <div
      className="fixed inset-x-3 bottom-20 sm:bottom-6 sm:left-auto sm:right-6 sm:w-80 z-[60] rounded-2xl bg-[#12101f] border border-[#FFB42D]/40 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.8)] p-4"
      data-testid="plyx-daily-bonus-card"
    >
      <button type="button" onClick={dismiss} data-testid="daily-bonus-dismiss"
        className="absolute top-2.5 right-2.5 text-white/40 hover:text-white">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl" aria-hidden="true">🎁</span>
        <div>
          <div className="font-extrabold text-white text-sm" data-testid="daily-bonus-title">
            Daily bonus — day {status.eligible_streak_day}
          </div>
          <div className="text-[11px] text-white/60">
            {status.eligible_streak_day > 1 ? `🔥 ${status.eligible_streak_day}-day streak — keep it alive!` : "Come back daily to grow your streak"}
          </div>
        </div>
      </div>
      <div className="flex gap-1 my-3" data-testid="daily-bonus-ladder">
        {table.map((coins, i) => {
          const d = i + 1;
          const isToday = d === day;
          const past = d < day;
          return (
            <div key={d}
              className={`flex-1 rounded-lg py-1.5 text-center ${
                isToday ? "bg-gradient-to-b from-[#FFB42D] to-[#FF8A2D] text-black"
                : past ? "bg-white/10 text-white/40"
                : "bg-white/[0.04] text-white/50"
              }`}>
              <div className="text-[9px] font-bold">D{d}{d === table.length ? "+" : ""}</div>
              <div className="text-[10px] font-extrabold">{coins}</div>
            </div>
          );
        })}
      </div>
      <button type="button" onClick={claim} disabled={claiming} data-testid="daily-bonus-claim"
        className="w-full py-2.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-extrabold text-sm disabled:opacity-50">
        {claiming ? "Claiming…" : `Claim ${status.next_reward_coins} coins`}
      </button>
    </div>
  );
};

export default DailyBonusCard;
